/**
 * TablePartField — thin FormNode adapter over TabularGrid.
 *
 * Merges the form-schema column order (Designer-authored `node.columns`)
 * with type metadata from `cfg.attributes[<tabular>].columns`, then hands the
 * result to TabularGrid as `TabularColumn[]`. All interactive behavior (typed
 * cells, ref lookup, calculator, sort/group/columns/Σ/resize, add/delete row)
 * lives in TabularGrid — this file only resolves "what column, what type".
 */
import React, { useMemo } from 'react'
import { useAppLocale } from '@/app/permissions/useAppLocale'
import { TabularGrid, type TabularColumn } from './tabular'
import type { FormNode } from '../designer/types/form.types'
import type { AttributeDef, AttributeType } from '../designer/services/configObjectApi'
import { resolveLabel } from './resolveLabel'

interface TablePartFieldProps {
  /** The Table FormNode from the form schema (carries title + column descriptors). */
  node: FormNode
  /** Current rows for this tabular section. */
  rows: Record<string, any>[]
  /** Replace rows. Caller persists into form state. */
  onChange: (next: Record<string, any>[]) => void
  /** Connection id — needed for ref-column lookups and tabular prefs scoping. */
  connectionId: string
  /** Owning object name — scopes tabular prefs (sort/group/columns/widths) per-object. */
  objectName: string
  /** Type metadata for each column, taken from cfg.attributes[<tabular>].columns. */
  columnAttrs: AttributeDef[]
  disabled?: boolean
  /** Open a referenced record — same dirty-confirm path as header ref fields. */
  onOpenRef?: (target: string, id: string) => void
  /** Fired when a cell's value is COMMITTED — threaded straight through to TabularGrid. */
  onCellCommit?: (rowIndex: number, colName: string, nextRows: Record<string, any>[]) => void
}

function resolveType(attr: AttributeDef | undefined, fallback: AttributeType = 'string'): AttributeType {
  const raw = String(attr?.attr_type || fallback).toLowerCase()
  // Designer tree historically stored "reference"; config API uses "ref".
  return (raw === 'reference' ? 'ref' : raw) as AttributeType
}

/**
 * Synthetic AttributeDef for a form column with no matching cfg attribute —
 * keeps TabularGrid's cell-type resolution working even when a Designer
 * form's column order drifts ahead of the object's saved config.
 */
function syntheticAttr(name: string, title: string): AttributeDef {
  return {
    name,
    synonym: title,
    attr_type: 'string',
    required: false,
    indexed: false,
    unique: false,
    hint: '',
    props: {},
    is_standard: false,
    order: 0,
  }
}

export const TablePartField: React.FC<TablePartFieldProps> = ({
  node,
  rows,
  onChange,
  connectionId,
  objectName,
  columnAttrs,
  disabled = false,
  onOpenRef,
  onCellCommit,
}) => {
  const { locale, defaultCode } = useAppLocale()

  // Merge form-schema column order with attribute type info from cfg.
  const columns: TabularColumn[] = useMemo(() => {
    const byName = new Map(columnAttrs.map((a) => [a.name.toLowerCase(), a]))
    const formCols = (node.columns as Array<Record<string, any>> | undefined) ?? []
    const out: TabularColumn[] = []

    const buildColumn = (leaf: string, title: string, attr: AttributeDef | undefined): TabularColumn => ({
      name: leaf,
      title,
      attr: attr
        ? { ...attr, name: leaf, synonym: title, attr_type: resolveType(attr) }
        : syntheticAttr(leaf, title),
    })

    if (formCols.length > 0) {
      for (const c of formCols) {
        const dataPath = String(c.dataPath || '')
        const formName = String(c.name || '')
        // Designer table columns often keep a generated name (Column7) while
        // dataPath carries the real attribute. Prefer dataPath or we fall
        // through to a synthetic string cell and the ref picker never appears.
        const raw = dataPath || formName
        if (!raw) continue
        // dataPath may be "Lines.Product" — match on the last segment.
        const leaf = raw.includes('.') ? raw.split('.').pop()! : raw
        const attr =
          byName.get(leaf.toLowerCase()) ??
          byName.get(formName.toLowerCase()) ??
          byName.get(dataPath.toLowerCase())
        // Prefer the form column's own synonyms map; fall back to the cfg
        // attribute's — a hand-built table section never gets one of its own.
        const title = resolveLabel(
          {
            synonyms: c.synonyms ?? attr?.synonyms,
            synonym: attr?.synonym,
            title: c.title,
            name: leaf,
          },
          locale,
          defaultCode,
        )
        out.push(buildColumn(leaf, title, attr))
      }
    } else {
      // Fallback: use cfg column order.
      for (const a of columnAttrs) {
        out.push(buildColumn(a.name, resolveLabel(a, locale, defaultCode), a))
      }
    }
    return out
  }, [node.columns, columnAttrs, locale, defaultCode])

  // Prefs are scoped per (connection, object, tabular) — prefer the schema
  // node's own name (matches the attribute name), fall back to the first
  // resolved column so prefs still work for oddly-shaped legacy forms.
  const tabularName = (node.name as string | undefined) || columns[0]?.name || 'table'

  const heading =
    node.titleVisible === false
      ? undefined
      : (node.title as string | undefined) || (node.name as string | undefined) || undefined

  return (
    <TabularGrid
      columns={columns}
      rows={rows}
      onChange={onChange}
      connectionId={connectionId}
      objectName={objectName}
      tabularName={tabularName}
      disabled={disabled}
      onOpenRef={onOpenRef}
      title={heading}
      onCellCommit={onCellCommit}
    />
  )
}
